/**
 * CollisionSystem - Handles collision detection between entities.
 * Operates directly on Koota Entity + traits (no adapters).
 */

import { ConfigService } from '@/config/ConfigService';
import {
  Collider,
  DeployableData,
  EnemyData,
  IsDead,
  Position,
  ProjectileData,
  ShockwaveData,
} from '@/ecs/traits';
import { EntityManager } from '@/managers/EntityManager';
import { distance, distanceSquared, Vector2 } from '@/utils';
import type { Entity } from 'koota';
import { singleton } from 'tsyringe';

/**
 * Collision detection result — all arrays contain raw Koota Entity references.
 */
export interface CollisionResult {
  /** Enemies colliding with player */
  playerEnemyCollisions: Entity[];
  /** Enemy projectiles hitting player */
  playerProjectileCollisions: Entity[];
  /** Player projectiles hitting enemies */
  projectileEnemyCollisions: Array<{ projectile: Entity; enemy: Entity }>;
  /** Pickups in player collection range */
  pickupCollisions: Entity[];
  /** Deployables triggered by enemies */
  deployableCollisions: Array<{ deployable: Entity; enemies: Entity[] }>;
  /** Shockwaves hitting the player */
  shockwavePlayerCollisions: Entity[];
  /** Shockwave-entity knockback pairs (ring passed through entity) */
  shockwaveEntityCollisions: Array<{ shockwave: Entity; entity: Entity }>;
  /** Enemies overlapping with other enemies */
  enemyEnemyCollisions: Array<{ enemyA: Entity; enemyB: Entity }>;
}

@singleton()
export class CollisionSystem {
  public constructor(
    private entityManager: EntityManager,
    private configService: ConfigService,
  ) {}

  public checkAll(): CollisionResult {
    const result: CollisionResult = {
      playerEnemyCollisions: [],
      playerProjectileCollisions: [],
      projectileEnemyCollisions: [],
      pickupCollisions: [],
      deployableCollisions: [],
      shockwavePlayerCollisions: [],
      shockwaveEntityCollisions: [],
      enemyEnemyCollisions: [],
    };

    const playerEntity = this.entityManager.getPlayerEntity();
    if (playerEntity.has(IsDead)) return result;

    const playerPos = playerEntity.get(Position)!;
    const playerRadius = playerEntity.get(Collider)!.radius;

    result.playerEnemyCollisions = this.checkPlayerEnemyCollisions(playerPos, playerRadius);
    result.playerProjectileCollisions = this.checkPlayerProjectileCollisions(
      playerPos,
      playerRadius,
    );
    result.projectileEnemyCollisions = this.checkProjectileEnemyCollisions();
    result.pickupCollisions = this.checkPickupCollisions(playerPos, playerRadius);
    result.deployableCollisions = this.checkDeployableCollisions();
    result.shockwavePlayerCollisions = this.checkShockwavePlayerCollisions(playerPos);
    result.shockwaveEntityCollisions = this.checkShockwaveEntityCollisions();
    result.enemyEnemyCollisions = this.checkEnemyEnemyCollisions();

    return result;
  }

  private checkPlayerEnemyCollisions(
    playerPos: { x: number; y: number },
    playerRadius: number,
  ): Entity[] {
    const collisions: Entity[] = [];
    const enemies = this.entityManager.getActiveEnemies();

    for (const enemy of enemies) {
      const d = enemy.get(EnemyData)!;
      if (d.phasing) continue;

      const ePos = enemy.get(Position)!;
      const eRadius = enemy.get(Collider)!.radius;
      const combinedRadius = playerRadius + eRadius;
      if (distanceSquared(playerPos, ePos) < combinedRadius * combinedRadius) {
        collisions.push(enemy);
      }
    }

    return collisions;
  }

  private checkPlayerProjectileCollisions(
    playerPos: { x: number; y: number },
    playerRadius: number,
  ): Entity[] {
    const collisions: Entity[] = [];
    const enemyProjectiles = this.entityManager.getActiveEnemyProjectiles();

    for (const proj of enemyProjectiles) {
      const pPos = proj.get(Position)!;
      const pRadius = proj.get(Collider)!.radius;
      const combinedRadius = playerRadius + pRadius;
      if (distanceSquared(playerPos, pPos) < combinedRadius * combinedRadius) {
        collisions.push(proj);
      }
    }

    return collisions;
  }

  private checkProjectileEnemyCollisions(): Array<{ projectile: Entity; enemy: Entity }> {
    const collisions: Array<{ projectile: Entity; enemy: Entity }> = [];
    const playerProjectiles = this.entityManager.getActivePlayerProjectiles();
    const enemies = this.entityManager.getActiveEnemies();

    for (const proj of playerProjectiles) {
      const pPos = proj.get(Position)!;
      const pRadius = proj.get(Collider)!.radius;
      const pd = proj.get(ProjectileData)!;

      for (const enemy of enemies) {
        const ePos = enemy.get(Position)!;
        const eRadius = enemy.get(Collider)!.radius;
        const combinedRadius = pRadius + eRadius;

        if (distanceSquared(pPos, ePos) < combinedRadius * combinedRadius) {
          // For piercing projectiles, check if already hit
          if (pd.pierce && pd.pierce.pierceCount > 0) {
            if (pd.pierce.hitEnemies.has(enemy.id())) {
              continue;
            }
            pd.pierce.hitEnemies.add(enemy.id());
            pd.pierce.pierceCount--;
          }

          collisions.push({ projectile: proj, enemy });

          // Non-piercing projectiles stop after first hit
          if (!pd.pierce || pd.pierce.pierceCount <= 0) {
            break;
          }
        }
      }
    }

    return collisions;
  }

  private checkPickupCollisions(
    playerPos: { x: number; y: number },
    playerRadius: number,
  ): Entity[] {
    const collisions: Entity[] = [];
    const pickups = this.entityManager.getActivePickups();

    for (const pickup of pickups) {
      const pPos = pickup.get(Position)!;
      const pRadius = pickup.get(Collider)!.radius;
      const combinedRadius = playerRadius + pRadius;
      if (distanceSquared(playerPos, pPos) < combinedRadius * combinedRadius) {
        collisions.push(pickup);
      }
    }

    return collisions;
  }

  private checkDeployableCollisions(): Array<{ deployable: Entity; enemies: Entity[] }> {
    const collisions: Array<{ deployable: Entity; enemies: Entity[] }> = [];
    const deployables = this.entityManager.getArmedDeployables();
    const enemies = this.entityManager.getActiveEnemies();

    for (const deployable of deployables) {
      const dd = deployable.get(DeployableData)!;
      const dPos = deployable.get(Position)!;
      const triggeredBy: Entity[] = [];

      for (const enemy of enemies) {
        const ePos = enemy.get(Position)!;
        const eRadius = enemy.get(Collider)!.radius;
        const combinedRadius = dd.triggerRadius + eRadius;
        if (distanceSquared(dPos, ePos) < combinedRadius * combinedRadius) {
          triggeredBy.push(enemy);
        }
      }

      if (triggeredBy.length > 0) {
        collisions.push({ deployable, enemies: triggeredBy });
      }
    }

    return collisions;
  }

  private checkEnemyEnemyCollisions(): Array<{ enemyA: Entity; enemyB: Entity }> {
    const collisions: Array<{ enemyA: Entity; enemyB: Entity }> = [];
    const enemies = this.entityManager.getActiveEnemies();

    for (let i = 0; i < enemies.length; i++) {
      const enemyA = enemies[i]!;
      const dA = enemyA.get(EnemyData)!;
      if (dA.phasing) continue;

      const posA = enemyA.get(Position)!;
      const radiusA = enemyA.get(Collider)!.radius;

      for (let j = i + 1; j < enemies.length; j++) {
        const enemyB = enemies[j]!;
        const dB = enemyB.get(EnemyData)!;
        if (dB.phasing) continue;

        const posB = enemyB.get(Position)!;
        const radiusB = enemyB.get(Collider)!.radius;
        const combinedRadius = radiusA + radiusB;

        if (distanceSquared(posA, posB) < combinedRadius * combinedRadius) {
          collisions.push({ enemyA, enemyB });
        }
      }
    }

    return collisions;
  }

  private checkShockwavePlayerCollisions(playerPos: { x: number; y: number }): Entity[] {
    const shockwaves = this.entityManager.getActiveShockwaves();
    const collisions: Entity[] = [];
    const ringWidth = this.configService.getEffectsConfig().shockwaves.ringWidth;

    for (const sw of shockwaves) {
      const sd = sw.get(ShockwaveData)!;
      if (sd.damageDealt) continue;

      const swPos = sw.get(Position)!;
      const dist = distance(swPos, playerPos);
      if (dist <= sd.currentRadius && dist >= sd.currentRadius - ringWidth) {
        collisions.push(sw);
      }
    }

    return collisions;
  }

  private checkShockwaveEntityCollisions(): Array<{ shockwave: Entity; entity: Entity }> {
    const shockwaves = this.entityManager.getActiveShockwaves();
    const collisions: Array<{ shockwave: Entity; entity: Entity }> = [];
    const ringWidth = this.configService.getEffectsConfig().shockwaves.ringWidth;
    const entities = this.entityManager.getKnockbackableEntities();

    for (const sw of shockwaves) {
      const sd = sw.get(ShockwaveData)!;
      const swPos = sw.get(Position)!;

      for (const entity of entities) {
        const id = entity.id();
        if (sd.knockedBackEntities.has(id)) continue;

        const ePos = entity.get(Position)!;
        const dist = distance(swPos, ePos);
        if (dist <= sd.currentRadius && dist >= sd.currentRadius - ringWidth) {
          collisions.push({ shockwave: sw, entity });
        }
      }
    }

    return collisions;
  }

  /**
   * Check if point is inside any enemy. Returns the entity or null.
   */
  public isPointInEnemy(position: Vector2): Entity | null {
    const enemies = this.entityManager.getActiveEnemies();

    for (const enemy of enemies) {
      const ePos = enemy.get(Position)!;
      const eRadius = enemy.get(Collider)!.radius;
      const dx = position.x - ePos.x;
      const dy = position.y - ePos.y;
      if (dx * dx + dy * dy <= eRadius * eRadius) {
        return enemy;
      }
    }

    return null;
  }
}
